import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { DialogHeader, dialogActionsSx, dialogPaperProps } from "./DialogParts";
import {
  outlinedButtonSx,
  primaryButtonSx,
} from "../../Organization/CodeSecurity/codeSecurityShared";
import {
  requestTrustCenterAccess,
  requestVisitorCode,
  verifyVisitorCode,
} from "../../../apis/trust-center-apis/trustCenterApis";

const STEPS = { documents: "documents", email: "email", code: "code" };

// Asking for locked documents, one step at a time:
//   1. documents: pick one or more → Next
//   2. email: who is asking (name, work email, company, why) → Next, which
//      emails a one-time code
//   3. code: enter it → Done, which sends the request to the admin
// The files stay locked until the admin grants access. The code proves the
// email is theirs (the API takes a request only from a verified email).
// `documents`: the locked documents ({ id, name, category }); `resourceId`
// starts ticked (the document they clicked). Without `documents` the first
// step is skipped and it asks for that one document.
const RequestAccessDialog = ({
  open,
  onClose,
  slug,
  companyName,
  resourceId,
  resourceName,
  documents = [],
  onSubmitted,
}) => {
  const theme = useTheme();
  const [picked, setPicked] = useState([]);
  const [step, setStep] = useState(STEPS.documents);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [reason, setReason] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [doneMessage, setDoneMessage] = useState("");
  const [doneStatus, setDoneStatus] = useState("");

  const choosing = documents.length > 0;

  useEffect(() => {
    if (!open) return;
    setStep(choosing ? STEPS.documents : STEPS.email);
    setName("");
    setEmail("");
    setCompany("");
    setReason("");
    setCode("");
    setBusy(false);
    setError("");
    setDoneMessage("");
    setDoneStatus("");
    setPicked(resourceId ? [resourceId] : []);
    // Reset only when the dialog opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const chosenIds = choosing ? picked : resourceId ? [resourceId] : [];
  const togglePick = (id) =>
    setPicked((current) =>
      current.includes(id) ? current.filter((x) => x !== id) : [...current, id]
    );
  const chosenLabel = choosing
    ? picked.length === 1
      ? documents.find((d) => d.id === picked[0])?.name
      : picked.length > 1
        ? `${picked.length} documents`
        : "Choose documents"
    : resourceName || "This file";

  const hostName = companyName || "this company";
  const steps = choosing
    ? [STEPS.documents, STEPS.email, STEPS.code]
    : [STEPS.email, STEPS.code];
  const stepTitles = {
    [STEPS.documents]: "Choose documents",
    [STEPS.email]: "Your details",
    [STEPS.code]: "Confirm your email",
  };
  const stepNumber = steps.indexOf(step) + 1;
  const goBack = () => {
    setError("");
    if (step === STEPS.code) {
      setCode("");
      setStep(STEPS.email);
    } else if (step === STEPS.email && choosing) setStep(STEPS.documents);
    else onClose();
  };
  const canGoOn =
    step === STEPS.documents
      ? chosenIds.length > 0
      : step === STEPS.email
        ? !!(
            name.trim() &&
            email.trim() &&
            reason.trim() &&
            chosenIds.length > 0
          )
        : code.trim().length >= 4;
  const goOn = () => {
    if (step === STEPS.documents) {
      setError("");
      setStep(STEPS.email);
    } else if (step === STEPS.email) sendCode();
    else confirmCode();
  };

  const note = (icon, title, body) => (
    <Stack
      direction="row"
      spacing={1.5}
      alignItems="flex-start"
      sx={{
        p: 1.5,
        borderRadius: "8px",
        border: `1px solid ${theme.customColors.border.main}`,
        backgroundColor: theme.customColors.background.cardHeader,
      }}
    >
      <Box
        sx={{
          width: 32,
          height: 32,
          flexShrink: 0,
          borderRadius: "8px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: theme.customColors.icons.primary,
          backgroundColor: theme.customColors.background.main,
          border: `1px solid ${theme.customColors.border.main}`,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        {title && (
          <Typography sx={{ fontWeight: 500, fontSize: 14, mb: 0.25 }}>
            {title}
          </Typography>
        )}
        <Typography
          variant="body2"
          sx={{ color: theme.customColors.text.secondary, lineHeight: 1.5 }}
        >
          {body}
        </Typography>
      </Box>
    </Stack>
  );

  const sendCode = async () => {
    setBusy(true);
    setError("");
    try {
      await requestVisitorCode({ slug, email });
      setStep(STEPS.code);
    } catch (err) {
      setError(err.message || "Could not send a code. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const confirmCode = async () => {
    setBusy(true);
    setError("");
    try {
      const session = await verifyVisitorCode({
        slug,
        email,
        code: code.trim(),
      });
      const result = await requestTrustCenterAccess({
        slug,
        name: name.trim() || email.split("@")[0],
        email,
        company,
        reason,
        resourceId: chosenIds[0],
        resourceIds: chosenIds,
        token: session.token,
      });
      setDoneMessage(result.message || "Your request was sent.");
      setDoneStatus(result.status || "");
      onSubmitted?.(result);
    } catch (err) {
      setError(err.message || "That code did not work. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={dialogPaperProps}
    >
      <DialogHeader
        title="Request access"
        subtitle={
          doneMessage
            ? undefined
            : `Step ${stepNumber} of ${steps.length}: ${stepTitles[step]}`
        }
        onClose={busy ? undefined : onClose}
      />
      <DialogContent dividers sx={{ pt: 2.5 }}>
        {doneMessage ? (
          <Stack spacing={2}>
            <Alert severity="success" sx={{ borderRadius: "8px" }}>
              {doneMessage}
            </Alert>
            <Typography
              variant="body2"
              sx={{ color: theme.customColors.text.secondary }}
            >
              {doneStatus === "sent"
                ? chosenIds.length > 1
                  ? "The files are attached to that email."
                  : "The file is attached to that email."
                : `${hostName} will email you ${
                    chosenIds.length > 1 ? "the documents" : "the document"
                  } if they approve the request.`}
            </Typography>
          </Stack>
        ) : (
          <Stack spacing={2}>
            {step === STEPS.documents &&
              note(
                <LockOutlinedIcon sx={{ fontSize: 18 }} />,
                null,
                `Pick the documents you need. ${hostName} reviews the request and emails them if they approve.`
              )}
            {step === STEPS.email &&
              note(
                <LockOutlinedIcon sx={{ fontSize: 18 }} />,
                chosenLabel,
                "Tell us who is asking. We'll email you a code to confirm the address."
              )}
            {step === STEPS.code &&
              note(
                <LockOutlinedIcon sx={{ fontSize: 18 }} />,
                "Check your email",
                <>
                  Enter the code sent to <strong>{email}</strong>, then click
                  Done to send your request.
                </>
              )}
            {error && (
              <Alert severity="error" sx={{ borderRadius: "8px" }}>
                {error}
              </Alert>
            )}
            {step === STEPS.documents && (
              <Box
                role="group"
                aria-label="Documents to request"
                sx={{
                  border: `1px solid ${theme.customColors.border.main}`,
                  borderRadius: "10px",
                  maxHeight: 320,
                  overflowY: "auto",
                  py: 0.5,
                }}
              >
                {documents.map((doc) => (
                  <FormControlLabel
                    key={doc.id}
                    sx={{ display: "flex", mx: 0, px: 0.75, py: 0.25 }}
                    control={
                      <Checkbox
                        size="small"
                        checked={picked.includes(doc.id)}
                        onChange={() => togglePick(doc.id)}
                      />
                    }
                    label={
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
                          {doc.name}
                        </Typography>
                        {doc.category && (
                          <Typography
                            variant="caption"
                            sx={{ color: theme.customColors.text.secondary }}
                          >
                            {doc.category}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                ))}
              </Box>
            )}
            {step === STEPS.email && (
              <>
                <TextField
                  label="Your name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  fullWidth
                  size="small"
                  disabled={busy}
                  autoFocus
                />
                <TextField
                  label="Work email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  fullWidth
                  size="small"
                  disabled={busy}
                />
                <TextField
                  label="Company"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  fullWidth
                  size="small"
                  disabled={busy}
                  helperText="Optional. Helps the company recognize you."
                />
                <TextField
                  label="Why do you need this"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  required
                  fullWidth
                  size="small"
                  multiline
                  minRows={2}
                  disabled={busy}
                  inputProps={{ maxLength: 500 }}
                />
              </>
            )}
            {step === STEPS.code && (
              <TextField
                label="Code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                required
                fullWidth
                size="small"
                disabled={busy}
                autoFocus
                placeholder="6-digit code"
                inputProps={{
                  inputMode: "numeric",
                  autoComplete: "one-time-code",
                  "aria-label": "Code",
                }}
                sx={{
                  "& input": { letterSpacing: "0.2em", fontWeight: 500 },
                }}
              />
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={dialogActionsSx(theme)}>
        {doneMessage ? (
          <Button
            variant="contained"
            onClick={onClose}
            sx={primaryButtonSx(theme)}
          >
            Close
          </Button>
        ) : (
          <>
            <Button
              variant="outlined"
              onClick={goBack}
              disabled={busy}
              sx={outlinedButtonSx(theme)}
            >
              {steps.indexOf(step) === 0 ? "Cancel" : "Back"}
            </Button>
            <Button
              variant="contained"
              onClick={goOn}
              disabled={busy || !canGoOn}
              sx={primaryButtonSx(theme)}
            >
              {busy
                ? step === STEPS.email
                  ? "Sending code…"
                  : "Sending…"
                : step === STEPS.code
                  ? "Done"
                  : "Next"}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default RequestAccessDialog;
